import { EXTENSION_NAMES, FILE_ERROR_MESSAGE, FILE_STATUS, MIME_TYPES, TFileStatus } from "@/constants/file";
import { TExtendFile } from "@/types/file";
import { uniqueArray } from "@/utils/pieces";
import { replaceStr } from "@/utils/string";
import { Accept } from "react-dropzone";

/**
 * Converts bytes to megabytes.
 *
 * @param {number} size - The size in bytes.
 * @returns {number} - The size in megabytes.
 */
export const convertByteToMb = (size: number): number => {
  return size / (1000 * 1000);
};

/**
 * Converts file size from kilobytes to megabytes.
 * @param size - Size in kilobytes.
 * @returns Size in megabytes.
 */
export const convertKbToMb = (size: number) => {
  return size / (1024 * 1024);
};

/**
 * Converts kilobytes to bytes.
 *
 * @param {number} size - The size in kilobytes.
 * @returns {number} - The size in bytes.
 */
export const convertKbToByte = (size: number): number => {
  return size * 1000;
};

/**
 * Converts megabytes to bytes.
 *
 * @param {number} size - The size in megabytes.
 * @returns {number} - The size in bytes.
 */
export const convertMbToByte = (size: number): number => {
  return size * (1000 * 1000);
};

export type TFileRules = Partial<{
  maxSize: number;
  minSize: number;
  acceptMimeTypes: string[];
  acceptFileTypesExtension: string[];
  maxFile: number;
}>;

/**
 * Evaluates the status of a file based on provided rules.
 * @param file - The file to check.
 * @param options - The rules to apply.
 * @returns The status of the file based on the rules.
 */
export const getFileStatusWithRules = (file: File, options: TFileRules): TFileStatus => {
  try {
    // Check if file size is less than minimum size if defined
    if (options.minSize !== undefined && file.size < options.minSize) {
      return FILE_STATUS.ERROR_MIN_SIZE;
    }
    // Check if file size exceeds maximum size if defined
    if (options.maxSize !== undefined && file.size > options.maxSize) {
      return FILE_STATUS.ERROR_MAX_SIZE;
    }
    // Check if file type is among the accepted MIME types
    if (options.acceptMimeTypes !== undefined && !options.acceptMimeTypes.includes(file.type)) {
      return FILE_STATUS.ERROR_ACCEPT_FILES;
    }
    // If all checks pass, return OK status
    return FILE_STATUS.OK;
  } catch {
    // Return a generic error status if any exception occurs
    return FILE_STATUS.ERROR;
  }
};

/**
 * Determines if a file has an error status.
 * @param file - The file to check.
 * @returns True if the file has an error status, otherwise false.
 */
export const isFileError = (file: TExtendFile) => {
  if (!file.status) return false;
  // Check if file status is one of the defined error statuses
  return [FILE_STATUS.ERROR, FILE_STATUS.ERROR_ACCEPT_FILES, FILE_STATUS.ERROR_MAX_SIZE, FILE_STATUS.ERROR_MIN_SIZE].includes(file.status);
};

/**
 * Retrieves the file extension corresponding to a MIME type.
 * @param mimeType - The MIME type to check.
 * @returns The file extension if found, otherwise null.
 */
export const getExtensionOfMimeType = (mimeType: string) => {
  // Find the MIME type entry in the MIME_TYPES object
  const found = Object.entries(MIME_TYPES).find(([_, mimeTypes]) => mimeTypes.includes(mimeType));
  if (!found) return null;
  const extensionKey = found[0];
  // Return the corresponding extension name
  return EXTENSION_NAMES[extensionKey] ?? null;
};

/**
 * Retrieves all file extensions corresponding to a list of MIME types.
 * @param mimeTypes - An array of MIME types.
 * @returns An array of unique file extensions.
 */
export const getExtensionsOfMimeTypes = (mimeTypes: string[]): string[] => {
  if (mimeTypes.some((item) => item.startsWith("."))) {
    return uniqueArray(mimeTypes.map((item) => item.replace(".", "")));
  }
  const extensionNames: string[] = [];
  mimeTypes.forEach((mimeType) => {
    const extensionName = getExtensionOfMimeType(mimeType);
    if (extensionName) extensionNames.push(extensionName);
  });
  return uniqueArray(extensionNames);
};

export const getExtensionDetailOfMimeType = (mimeTypes: string[]) => {
  const mimeToExtMap: Record<string, string> = {
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "application/x-ole-storage": "doc",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/xml": "xml",
    "text/xml": "xml",
    "text/csv": "csv",
    "text/plain": "txt",
    "image/jpg": "jpg",
    "image/jpeg": "jpeg",
    "image/gif": "gif",
    "image/png": "png",
    "application/vnd.rar": "rar",
    "application/x-rar": "rar",
    "multipart/x-rar": "rar",
    ".rar": "rar",
    "application/x-rar-compressed": "rar",
    "application/zip": "zip",
    "application/vnd.ms-powerpoint": "ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
  };

  const extensions = mimeTypes.map((mime) => mimeToExtMap[mime as keyof typeof mimeToExtMap] || "").filter((ext) => ext !== "");

  return Array.from(new Set(extensions));
};

/**
 * Converts MIME types to a format acceptable by react-dropzone.
 * @param mimeTypes - An array of MIME types.
 * @returns An object where keys are MIME types and values are empty arrays.
 */
export const covertMimeTypesToAccepts = (mimeTypes?: string[]) => {
  if (!mimeTypes) return undefined;
  // Create an object with MIME types as keys and empty arrays as values
  return mimeTypes.reduce((cur, mimeType) => ({ ...cur, [mimeType]: [] }), {} as Accept);
};

/**
 * Extracts the file name from the file path.
 *
 * @param {string} [filePath=""] - The file path.
 * @returns {string | null} - The file name, or null if not found. Ex: 1724653683544_sample.pdf => sample.pdf
 */
export const getFileNameFromPath = (filePath: string = ""): string | null => {
  const fileName = filePath.split("/").pop();
  if (!fileName) return null;
  const [_, ...fileNameWithoutTimestamp] = fileName.split("_");
  if (fileNameWithoutTimestamp) return fileNameWithoutTimestamp.join("_");
  return null;
};

/**
 * Generates an error message for a file based on its status and provided rules.
 * @param file - The file to check.
 * @param rules - The rules to apply.
 * @returns An error message if the file has an error status, otherwise an empty string.
 */
export const getFileErrorMessage = (file: TExtendFile, rules: TFileRules) => {
  if (isFileError(file)) {
    // Generate the error message based on the file status and rules
    return replaceStr(FILE_ERROR_MESSAGE[file.status], {
      maxSize: rules.maxSize,
      minSize: rules.minSize,
      extensions: getExtensionsOfMimeTypes(rules.acceptMimeTypes ?? []).join("、"),
    });
  }
  return "";
};

export const checkActualPdfFile = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    if (file.type !== "application/pdf") {
      resolve(false);
      return;
    }

    // check magic number of PDF
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      const buffer = e.target?.result as ArrayBuffer;
      const arr = new Uint8Array(buffer).subarray(0, 4);
      // Convert Uint8Array to string without spread operator
      let header = "";
      for (let i = 0; i < arr.length; i++) {
        header += String.fromCharCode(arr[i]);
      }
      resolve(header.includes("%PDF"));
    };
    fileReader.onerror = () => {
      resolve(false);
    };
    fileReader.readAsArrayBuffer(file.slice(0, 4));
  });
};

export const checkFileListWhetherIsActualPdf = async (files: File[]): Promise<boolean> => {
  try {
    const invalidPdf = await Promise.all(
      files
        .filter((file) => file.type === "application/pdf")
        .map(async (file) => {
          const isPdf = await checkActualPdfFile(file);
          return !isPdf;
        }),
    ).then((results) => results.some((result) => result));

    return invalidPdf;
  } catch (error) {
    console.error("Error checking PDF files:", error);
    return true;
  }
};

export const parseFilePath = (filePath?: string | null): { name: string; path: string }[] => {
  if (!filePath) return [];

  try {
    const parsed = JSON.parse(filePath);

    if (Array.isArray(parsed)) {
      return parsed.filter((f) => f && f.path);
    }

    if (parsed && parsed.path) {
      return [parsed];
    }
  } catch {
    if (typeof filePath === "string" && filePath.trim() !== "") {
      return [
        {
          name: filePath.split("/").pop() || "file",
          path: filePath,
        },
      ];
    }
  }

  return [];
};
